<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
  >
    <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
      <h3 class="text-xl font-bold mb-4">Edit Sample</h3>

      <!-- Sample Type (dynamic dropdown) -->
      <div class="mb-4">
        <label class="block font-medium text-gray-700">Sample Type
          <span class="text-xs text-gray-500">(original: {{ originalSampleType }})</span>
        </label>
        <select
          v-model="form.sample_type"
          class="w-full border rounded p-2 bg-white focus:border-custom-yellow focus:ring focus:ring-custom-yellow"
        >
          <option value="">Select Sample Type</option>
          <option v-for="(sample) in samples" :key="sample.id">
            {{ sample.name }}
          </option>
        </select>
      </div>

      <!-- Sample Status -->
      <div class="mb-4">
        <label class="block font-medium text-gray-700">
          Sample Status <span class="text-red-500">*</span>
        </label>
        <select
          v-model="form.status"
          class="mt-1 block w-full rounded border-gray-300 focus:border-custom-yellow focus:ring focus:ring-custom-yellow"
        >
          <option
            v-for="status in statuses"
            :key="status.id"
            :value="status.label"
          >
            {{ status.label }}
          </option>
        </select>
      </div>

      <!-- Sample Condition -->
      <div class="mb-6">
        <label class="block font-medium text-gray-700">Sample Condition</label>
        <textarea
          v-model="form.condition"
          rows="3"
          class="mt-1 block w-full rounded border-gray-300 focus:border-custom-yellow focus:ring focus:ring-custom-yellow"
          placeholder="Enter condition notes..."
        />
      </div>

      <div class="flex justify-end space-x-3">
        <button
          @click="$emit('close')"
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          @click="submit"
          :disabled="saving"
          class="px-4 py-2 bg-custom-yellow text-white rounded hover:bg-yellow-600 disabled:opacity-50"
        >
          {{ saving ? 'Updating...' : 'Update' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from '@/services/api';
import { useToast } from 'vue-toast-notification';

export default {
  name: 'EditLabSample',

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    sample: {
      type: Object,
      required: true,
    },
  },

  emits: ['close', 'updated'],

  setup() {
    const toast = useToast({
      position: 'top-right',
      duration: 3000,
      dismissible: true,
      pauseOnHover: true,
      type: 'default',
    });
    return { toast };
  },

  data() {
    return {
      saving: false,
      loadingSamples: false,
      samples: [],
      form: {
        sample_type: '',
        status: '',
        condition: '',
      },
      statuses: [
        { id: 1, label: 'Collected' },
        { id: 2, label: 'Recollect' },
      ],
    };
  },

  computed: {
    originalSampleType() {
      return this.sample.test_type?.sample?.name || 'N/A'
    }
  },

  watch: {
    sample: {
      handler(newSample) {
        if (newSample) {
          this.form.status = newSample.status?.label ?? '';
          this.form.condition = newSample.sample_condition ?? '';
          // Use override if exists, otherwise fallback
          this.form.condition = newSample.sample_type_override || newSample.test_type?.sample?.name
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    async fetchSamples() {
      this.loadingSamples = true;

      try {
        const response = await apiClient.getLabSamples();

        if (response.success) {
          this.samples = response.data
          this.toast.success(response.message)
        } else {
          this.toast.error('Failed to fetch samples.')
        }
      } catch (e) {
        this.toast.error(e.message || 'Request failed.')
        console.log('fetching lab samples failed', e)
      } finally {
        this.loadingSamples = false;
      }
    },
    async submit() {
      if (!this.form.status) {
        this.toast.error('Please select a status');
        return;
      }

      this.saving = true;
      try {
        const payload = {
          id: this.sample.id,
          status: this.form.status,
          sample_condition: this.form.condition,
          sample_type: this.form.sample_type,
          userId: localStorage.getItem('UserId') || 'unknown',
        };

        const response = await apiClient.updateLabSample(payload);

        if (response.success) {
          this.toast.success('Sample updated successfully');
          this.$emit('updated');
          this.$emit('close');
        } else {
          this.toast.error(response.message ?? 'Failed to update sample');
        }
      } catch (err) {
        this.toast.error(err.message ?? 'Network error');
        console.error('updateLabSample error →', err);
      } finally {
        this.saving = false;
      }
    },
  },
  async mounted() {
    await this.fetchSamples();
  }
};

</script>

<style scoped>
/* component‑specific styles */
</style>